import React from "react";

import LockIcon from "@mui/icons-material/Lock";
import {
  Stack,
  Button,
  Typography,
  StackProps,
  Box,
  Tooltip,
} from "@mui/material";

interface SettingSwitchProps {
  isOn: boolean;
  locked?: boolean;
  onChange: (on: boolean) => void;
  tooltip?: string;
}

interface SettingSwitchOptionProps extends StackProps {
  text: string;
  selected?: boolean;
  locked?: boolean;
}

const DEFAULT_LOCKED_TOOLTIP = "Enforced by your Workspace";

const getBackgroundColor = (checked: boolean, locked?: boolean) => {
  if (locked && checked) {
    return "shades.primary.400";
  } else if (!locked && checked) {
    return "shades.primary.500";
  } else if (locked && !checked) {
    return "shades.neutral.400";
  }

  return "shades.neutral.500";
};

const SettingSwitchOption = ({
  text,
  selected,
  locked,
}: SettingSwitchOptionProps) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: "50%",
        height: "100%",
        borderRadius: "8px",
        color: selected ? "#FFF" : "shades.neutral.400",
        zIndex: 2,
      }}
      spacing={0.5}
    >
      <Typography variant="bodySecondaryMed">{text}</Typography>
      {locked && selected && (
        <LockIcon sx={{ width: "10px", height: "10px" }} />
      )}
    </Stack>
  );
};

const SettingSwitch = ({
  isOn,
  locked,
  onChange,
  tooltip = DEFAULT_LOCKED_TOOLTIP,
}: SettingSwitchProps) => {
  const handleChange = () => {
    if (locked) return;

    return onChange(!isOn);
  };

  const component = (
    <Button
      sx={{
        width: "120px",
        minWidth: "120px",
        height: "38px",
        padding: 0,
        margin: 0,
        backgroundColor: "shades.neutral.50",
        "&:hover": {
          backgroundColor: "shades.neutral.50",
        },
      }}
      variant="text"
      disabled={locked}
      disableRipple
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
        }}
        onClick={handleChange}
      >
        <Box
          sx={{
            position: "absolute",
            width: "50%",
            height: "100%",
            borderRadius: "8px",
            backgroundColor: getBackgroundColor(isOn, locked),
            transform: isOn ? "translateX(100%)" : "0",
            transition: "transform 0.2s ease",
          }}
        />
        <SettingSwitchOption text="OFF" selected={!isOn} locked={locked} />
        <SettingSwitchOption text="ON" selected={isOn} locked={locked} />
      </Stack>
    </Button>
  );

  if (locked) {
    // extra box because tooltip needs a non-disabled element to clone
    return (
      <Tooltip arrow placement="top" title={tooltip}>
        <Box sx={{ width: "120px" }}>{component}</Box>
      </Tooltip>
    );
  }

  return component;
};

export default SettingSwitch;
